import { h, render } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'

import type { QaColor, QaPageStatus, QaStatusArtifact } from './types'
import type { QaMeterHandle, QaMeterOptions } from './index'
import { resolveStrings } from './i18n'
import { resolvePage } from './resolver'
import { QA_METER_STYLES } from './styles'
import { Pastille } from './Pastille'
import { TestPanel } from './TestPanel'
import { SitemapModal } from './SitemapModal'

/** The custom event the patched History API dispatches so the meter can
 *  re-resolve the current page on SPA navigations (pushState/replaceState
 *  don't fire `popstate`). Module-scoped name so every instance listens to
 *  the same channel. */
const LOCATION_CHANGE_EVENT = 'mqa:locationchange'

// ── History API patch (module-level, ref-counted) ──────────────────────────
// We monkey-patch pushState/replaceState ONCE across all instances so a
// `mqa:locationchange` CustomEvent fires on SPA navigations. A ref-count lets
// the LAST disposed instance restore the originals; guarding on `patched`
// prevents a second instance from double-wrapping (which would dispatch twice
// and, worse, capture an already-wrapped "original").
let historyPatched = false
let historyRefCount = 0
let originalPushState: History['pushState'] | null = null
let originalReplaceState: History['replaceState'] | null = null

function patchHistory(): void {
  historyRefCount++
  if (historyPatched) return
  historyPatched = true
  originalPushState = history.pushState
  originalReplaceState = history.replaceState
  const fire = (): void => {
    window.dispatchEvent(new CustomEvent(LOCATION_CHANGE_EVENT))
  }
  history.pushState = function patchedPushState(
    this: History,
    ...args: Parameters<History['pushState']>
  ) {
    const ret = originalPushState!.apply(this, args)
    fire()
    return ret
  }
  history.replaceState = function patchedReplaceState(
    this: History,
    ...args: Parameters<History['replaceState']>
  ) {
    const ret = originalReplaceState!.apply(this, args)
    fire()
    return ret
  }
}

function unpatchHistory(): void {
  if (historyRefCount > 0) historyRefCount--
  if (historyRefCount > 0 || !historyPatched) return
  historyPatched = false
  if (originalPushState) history.pushState = originalPushState
  if (originalReplaceState) history.replaceState = originalReplaceState
  originalPushState = null
  originalReplaceState = null
}

/** Default offset, matching the donor `qa-pastille.vue` (sits near the feedback
 *  FAB). `.qa-pastille` anchors bottom-right via the `--mqa-right`/`--mqa-bottom`
 *  custom properties, which default to `1.5rem` in the stylesheet. CSS custom
 *  properties inherit from the host element THROUGH the shadow boundary, so a
 *  host can nudge the pastille by setting those vars on the host (done below
 *  when `options.position` is provided) — setting `host.style.right/bottom`
 *  directly would NOT work, since `.qa-pastille` is itself `position: fixed`
 *  and anchors to the viewport, not the host. */

// ── Format validation ───────────────────────────────────────────────────────
const QA_ARTIFACT_FORMAT = 1

function isValidArtifact(value: unknown): value is QaStatusArtifact {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { format?: unknown }).format === QA_ARTIFACT_FORMAT
  )
}

let warnedBadFormat = false
function warnBadFormatOnce(): void {
  if (warnedBadFormat) return
  warnedBadFormat = true
  if (typeof console !== 'undefined') {
    console.warn('[mqa] QA Meter artifact has an unsupported `format` — refusing to mount.')
  }
}

const NOOP_HANDLE: QaMeterHandle = {
  open() {},
  close() {},
  dispose() {},
}

/** Every path_pattern an artifact knows — `pages[].page?.path_pattern` ∪
 *  `sitemap[].path_pattern`. Feeds the default routerless resolver. */
function patternsFromArtifact(artifact: QaStatusArtifact): string[] {
  const set = new Set<string>()
  for (const page of artifact.pages) {
    if (page.page) set.add(page.page.path_pattern)
  }
  for (const node of artifact.sitemap) {
    set.add(node.path_pattern)
  }
  return [...set]
}

interface Resolved {
  state: 'known' | 'no-tests' | 'unknown-page'
  color: QaColor
  pattern: string | null
  page: QaPageStatus | null
}

const UNKNOWN: Resolved = { state: 'unknown-page', color: 'gray', pattern: null, page: null }

/** State machine (task spec): a matched `pages[]` entry → known/its color;
 *  a pattern present only in the sitemap → no-tests/gray; no match (or no
 *  artifact yet) → unknown-page/gray. Never throws. */
function resolveCurrent(artifact: QaStatusArtifact | null, pattern: string | null): Resolved {
  if (!artifact || !pattern) return UNKNOWN
  const page = artifact.pages.find((p) => p.page && p.page.path_pattern === pattern)
  if (page && page.page) {
    return { state: 'known', color: page.color, pattern, page }
  }
  const inSitemap = artifact.sitemap.some((n) => n.path_pattern === pattern)
  if (inSitemap) {
    return { state: 'no-tests', color: 'gray', pattern, page: null }
  }
  return { state: 'unknown-page', color: 'gray', pattern, page: null }
}

export function createQaMeter(options: QaMeterOptions): QaMeterHandle {
  const { source } = options
  const strings = resolveStrings(options.locale ?? 'fr')

  // For an INLINE artifact we must validate synchronously at create time, so a
  // wrong-format object never produces a host (the test asserts this after a
  // single microtask).
  const inlineArtifact = typeof source === 'object' ? source : null
  if (inlineArtifact && !isValidArtifact(inlineArtifact)) {
    warnBadFormatOnce()
    return NOOP_HANDLE
  }

  // Loaded artifact cache + lazy loader state. Inline artifacts are ready now;
  // string/function sources load lazily on first open/hover, exactly once.
  let artifact: QaStatusArtifact | null = inlineArtifact
  let loadStarted = false
  let loadPromise: Promise<void> | null = null

  function load(): void {
    if (artifact || loadStarted) return
    loadStarted = true
    const got: Promise<unknown> =
      typeof source === 'function'
        ? source()
        : typeof source === 'string'
          ? fetch(source).then((r) => r.json())
          : Promise.resolve(source)
    loadPromise = got
      .then((value) => {
        if (isValidArtifact(value)) {
          artifact = value
          forceRender()
        } else {
          warnBadFormatOnce()
        }
      })
      .catch(() => {
        // Never throw to the host: stay gray.
      })
  }

  // ── Host + shadow root + styles (mirrors widget/mount.tsx) ────────────────
  const host = document.createElement('div')
  host.setAttribute('data-mqa-host', '')
  host.setAttribute('data-mqa-size', options.size ?? 'md')
  // Offset knobs as CSS custom properties: they inherit through the shadow
  // boundary down to `.qa-pastille`, which reads them with a 1.5rem fallback.
  // Only set each when provided so the stylesheet default applies otherwise.
  if (options.position?.right !== undefined) {
    host.style.setProperty('--mqa-right', `${options.position.right}px`)
  }
  if (options.position?.bottom !== undefined) {
    host.style.setProperty('--mqa-bottom', `${options.position.bottom}px`)
  }
  document.body.appendChild(host)

  const shadow = host.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = QA_METER_STYLES
  shadow.appendChild(style)
  const mountPoint = document.createElement('div')
  shadow.appendChild(mountPoint)

  // Imperative re-render hook into the Preact tree. The container component
  // registers its dispatcher here so the loader/navigation callbacks can poke
  // it without React-ish prop threading.
  let forceRender: () => void = () => {}

  function getCurrentPattern(): string | null {
    if (options.getCurrentPage) return options.getCurrentPage()
    if (!artifact) return null
    return resolvePage(window.location.pathname, patternsFromArtifact(artifact))
  }

  function Container() {
    // A bump-counter is the simplest "force re-render on external change"
    // primitive (loader resolves, navigation, open/close).
    const [tick, setTick] = useState(0)
    void tick
    const bump = useCallback(() => setTick((n) => n + 1), [])
    forceRender = bump

    // open/close lives in module scope (so the handle can drive it) but we
    // read it via a closure variable bumped through `forceRender`.
    const resolved = resolveCurrent(artifact, getCurrentPattern())

    const meta = artifact
      ? {
          generated_at: artifact.generated_at,
          app_version: artifact.app_version,
          environment: artifact.environment,
        }
      : { generated_at: '', app_version: '', environment: '' }

    const onHover = useCallback(() => {
      // Lazy-load on first hover too (not just open), so the panel shows real
      // data before the user clicks.
      load()
    }, [])

    const onOpenModal = useCallback(() => {
      load()
      uiOpen = true
      bump()
    }, [])

    const closeModal = useCallback(() => {
      uiOpen = false
      bump()
    }, [])

    // Re-resolve current page on navigation. popstate covers back/forward;
    // the patched History API covers SPA pushState/replaceState.
    useEffect(() => {
      const onNav = (): void => bump()
      window.addEventListener('popstate', onNav)
      window.addEventListener(LOCATION_CHANGE_EVENT, onNav)
      return () => {
        window.removeEventListener('popstate', onNav)
        window.removeEventListener(LOCATION_CHANGE_EVENT, onNav)
      }
    }, [])

    return h(
      'div',
      {
        class: 'qa-pastille-wrap',
        // Re-entering the wrap (the dot OR the panel above it — both live
        // inside) cancels a pending close; leaving schedules a debounced close.
        onMouseEnter: cancelHoverClose,
        onMouseLeave: scheduleHoverClose,
      },
      uiHover
        ? h(
            'div',
            { class: 'qa-panel-anchor' },
            h(TestPanel, { page: resolved.page, state: resolved.state, meta, strings }),
          )
        : null,
      h(Pastille, {
        color: resolved.color,
        state: resolved.state,
        strings,
        onHover: () => {
          cancelHoverClose()
          uiHover = true
          onHover()
          bump()
        },
        onOpenModal,
      }),
      uiOpen && artifact
        ? h(SitemapModal, {
            artifact,
            currentPattern: resolved.pattern,
            strings,
            onClose: closeModal,
          })
        : null,
    )
  }

  // UI-local flags kept in closure scope so the imperative handle (open/close)
  // can flip them and `forceRender()` reflects the change. The container reads
  // them on each render.
  let uiOpen = false
  let uiHover = false

  // Closing the hover panel is debounced (rather than firing on the raw
  // mouseleave) so sweeping the cursor across the small gap between the dot and
  // the panel — or a jittery mouse at the dot's edge — doesn't unmount it
  // mid-sweep. Any mouseenter back into the wrap cancels the pending close.
  let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null
  const HOVER_CLOSE_MS = 140
  function cancelHoverClose(): void {
    if (hoverCloseTimer !== null) {
      clearTimeout(hoverCloseTimer)
      hoverCloseTimer = null
    }
  }
  function scheduleHoverClose(): void {
    cancelHoverClose()
    hoverCloseTimer = setTimeout(() => {
      hoverCloseTimer = null
      if (disposed) return
      uiHover = false
      forceRender()
    }, HOVER_CLOSE_MS)
  }

  function mountTree(): void {
    render(h(Container, {}), mountPoint)
  }

  // Initial mount.
  mountTree()

  patchHistory()

  let disposed = false

  return {
    open() {
      if (disposed) return
      load()
      uiOpen = true
      forceRender()
    },
    close() {
      if (disposed) return
      uiOpen = false
      forceRender()
    },
    dispose() {
      if (disposed) return
      disposed = true
      cancelHoverClose()
      render(null, mountPoint)
      host.remove()
      unpatchHistory()
      void loadPromise
    },
  }
}
